import {Component, computed, inject, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Role} from '@models/user.interface';
import {Router, RouterLink} from '@angular/router';
import {AuthService} from '@services/auth.service';
import {AuthCreationForm, AuthCreationResponse} from '@models/auth.interface';
import {NotificationService} from '@services/frontend/notification.service';

@Component({
  selector: 'app-register',
  imports: [
    FormsModule,
    RouterLink
  ],
  templateUrl: './register.html'
})
export class Register {

  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private router = inject(Router);

  firstName: WritableSignal<string> = signal('');
  lastName: WritableSignal<string> = signal('');
  email: WritableSignal<string> = signal('');
  password: WritableSignal<string> = signal('');
  confirmPassword: WritableSignal<string> = signal('');
  role: WritableSignal<Role> = signal(Role.EXECUTIVE);
  cabinetName: WritableSignal<string> = signal('');
  cabinetPhone: WritableSignal<string> = signal('');
  cabinetEmail: WritableSignal<string> = signal('');

  error: WritableSignal<string> = signal("");
  loading: WritableSignal<boolean> = signal(false);
  registered: WritableSignal<boolean> = signal(false);

  private passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  isPasswordValid = computed(() =>
    this.passwordRegex.test(this.password())
  );

  passwordsMatch = computed(() =>
    this.password() === this.confirmPassword()
  );

  isFormValid = computed(() =>
    this.firstName().trim().length > 0 &&
    this.lastName().trim().length > 0 &&
    this.email().trim().length > 0 &&
    this.isPasswordValid() &&
    this.passwordsMatch() &&
    (this.role() !== Role.ACCOUNTANT ||
      (this.cabinetName().trim().length > 0
        && this.cabinetPhone().trim().length > 0
        && this.cabinetEmail().trim().includes('@')))
  );

  register(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.loading.set(true);
    this.error.set("");

    const payload: AuthCreationForm = {
      fullname: `${this.firstName()} ${this.lastName()}`,
      email: this.email(),
      password: this.password(),
      role: this.role(),
      ...(this.role() === Role.ACCOUNTANT
        ? {
          cabinetName: this.cabinetName().trim(),
          cabinetPhone: this.cabinetPhone().trim(),
          cabinetEmail: this.cabinetEmail().trim(),
        }
        : {}),
    };

    this.authService.register(payload).subscribe({
      next: (_value: AuthCreationResponse) => {
        this.loading.set(false);
        this.registered.set(true);
      },
      error: (err) => {
        this.error.set(err.error);
        this.loading.set(false);
      }
    })
  }

  protected readonly Role = Role;
}
